// The part of the Argo API every product speaks: the fetch plumbing, who the
// caller is, their step-up PIN, their contact details, and registration —
// which is one account across the exchange and insurance alike.
//
// Every response carries `ok`; failures are thrown as Error(message) so callers
// only handle the happy path.
import { config } from './config'

export const API_BASE = config.apiBase

// Bearer token supplier, installed by the auth provider once the user signs in.
let tokenProvider: () => string | undefined = () => undefined
export function setTokenProvider(fn: () => string | undefined) { tokenProvider = fn }

export type Me = { subject: string; username: string; account: string; roles: string[]; auth: 'oidc' | 'off'; pin: { set: boolean; locked_until: string; failed_attempts: number } }

type Envelope<T> = ({ ok: true } & T) | { ok: false; message: string }

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = tokenProvider()
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init?.headers ?? {}) },
  })
  let body: Envelope<T>
  try {
    body = (await res.json()) as Envelope<T>
  } catch {
    throw new Error(`HTTP ${res.status}`)
  }
  if (!body.ok) throw new Error(body.message || `HTTP ${res.status}`)
  return body as T
}

/** A multipart POST (a claim's evidence); the browser sets the boundary itself. */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  const token = tokenProvider()
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  let body: Envelope<T>
  try {
    body = (await res.json()) as Envelope<T>
  } catch {
    throw new Error(`HTTP ${res.status}`)
  }
  if (!body.ok) throw new Error(body.message || `HTTP ${res.status}`)
  return body as T
}

/** The signed-in caller's bearer token, for a download the browser performs itself. */
export const authToken = () => tokenProvider()

export const get = <T,>(path: string) => call<T>(path)
export const post = <T,>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body) })
export const del = <T,>(path: string) => call<T>(path, { method: 'DELETE' })

export const api = {
  health: () => get<{ service: string; uptime: number; sha: string | null }>('/health'),
  me: () => get<Me>('/me'),
  pinStatus: () => get<{ set: boolean; locked_until: string; failed_attempts: number }>('/pin'),
  setPin: (pin: string, current?: string) => post<{ set: boolean }>('/pin', { pin, current }),

  /** Confirming, or changing, the contact details on the signed-in account. */
  profile: () => get<Profile>('/profile'),
  confirmEmail: (email?: string, pin?: string) =>
    post<{ sent_to: string; changing: boolean }>('/profile/email', { email, pin }),
  confirmPhone: (phone?: string, pin?: string) =>
    post<{ changing: boolean; phone: PhoneSession }>('/profile/phone', { phone, pin }),
}

/** What verify.mn needs shown: its instruction verbatim, and the tap-to-send link. */
export type PhoneSession = {
  session_id: string; shortcode: string; code: string
  sms_uri: string; instruction: string; expires_at: string
}

export type Profile = {
  account: string
  email: string | null; email_verified: boolean
  phone: string | null; phone_verified: boolean
  can: { email: boolean; phone: boolean }
}

export type RegisterInput = {
  username: string; regNo: string; firstName: string; lastName: string
  email: string; phone: string; password: string
  verify: 'email' | 'phone'
}

export type Registered = {
  account: string
  verify: 'email' | 'phone'
  phone?: PhoneSession
  email?: { sent_to: string }
}

/** Public routes — these are the only calls made before anyone has a token. */
export type RegisterOptions = { enabled: boolean; confirm: { email: boolean; phone: boolean } }

export const registration = {
  options: () => get<RegisterOptions>('/register/options'),
  /** Is this username free, and if not, why? */
  checkUsername: (value: string) =>
    get<{ username?: string; available: boolean; reason?: string }>(`/register/username?value=${encodeURIComponent(value)}`),
  submit: (body: RegisterInput) => post<Registered>('/register', body),
  available: (q: Partial<Record<'email' | 'phone' | 'reg_no', string>>) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v) p.set(k, v)
    return get<{ email?: boolean; phone?: boolean; reg_no?: boolean }>(`/register/available?${p}`)
  },
  confirmEmail: (token: string) => post<{ account: string }>('/register/verify-email', { token }),

  /** Forgotten password: prove the channel already on file, then set a new one. */
  resetStart: (identifier: string, channel: 'email' | 'phone') =>
    post<{ channel: 'email' | 'phone'; sent_to?: string; phone?: PhoneSession }>('/register/reset', { identifier, channel }),
  resetPollPhone: (sessionId: string) =>
    post<{ status: 'PENDING' | 'VERIFIED' | 'EXPIRED'; verified: boolean; token?: string }>('/register/reset/phone', { session_id: sessionId }),
  resetComplete: (token: string, password: string) =>
    post<{ account: string }>('/register/reset/complete', { token, password }),
  checkPhone: (sessionId: string) =>
    post<{ status: 'PENDING' | 'VERIFIED' | 'EXPIRED'; verified: boolean }>('/register/verify-phone', { session_id: sessionId }),
}

export function fmt(n: number | string, digits = 0): string {
  const v = Number(n)
  if (!Number.isFinite(v)) return '-'
  return v.toLocaleString(undefined, { maximumFractionDigits: digits })
}
